import { useState } from "react";
import { ApiError, type InstanceCalendarSourceView } from "@keydials/api-client";
import { api, getAccessToken } from "../auth/AuthContext";
import { Modal } from "../components/chrome/Modal";
import { APPOINTMENT_LENGTHS, sourceLabel } from "./addOnDay";

const field: React.CSSProperties = {
  font: "inherit",
  fontSize: 14,
  padding: "8px 11px",
  borderRadius: "var(--radius-sm)",
  border: "var(--border-w) solid var(--border)",
  background: "var(--surface)",
  color: "var(--text)",
  minWidth: 0,
};

/** KEY-821: one of my appointments, as the change form starts from. `start` is "HH:mm" in the dial's zone. */
export interface AppointmentBeingChanged {
  itemKey: string;
  title: string;
  start: string | null;
  /** 0 for all day. */
  minutes: number;
  location: string | null;
}

const label: React.CSSProperties = { display: "flex", flexDirection: "column", gap: 4, fontSize: 12.5, color: "var(--text-muted)" };

/**
 * KEY-820: "Add" on a day, for someone whose calendar on this dial takes appointments. Two ways:
 * an appointment in their own calendar (written there, then read back onto the dial like any other event), or an
 * item just on this dial (the old behaviour). The appointment starts when the time of day does, for an hour, unless
 * changed. Resolves with the new item's key — the appointment's, once the calendar has been read back — or null.
 *
 * KEY-821: given `editing`, it changes one of the person's own appointments instead — straight to the form, filled in,
 * and saved to the same calendar it came from.
 */
export function AddOnDayDialog({
  instanceId,
  segmentKey,
  dayLabel,
  defaultStart,
  sources,
  itemWord,
  onJustDial,
  onDone,
  editing,
}: {
  instanceId: string;
  segmentKey: string;
  /** "Tomorrow morning" — what the dialog says it's adding to. */
  dayLabel: string;
  defaultStart: string | null;
  sources: InstanceCalendarSourceView[];
  itemWord: string;
  onJustDial: () => void;
  onDone: (itemKey: string | null) => void;
  /** KEY-821: the appointment being changed. */
  editing?: AppointmentBeingChanged;
}) {
  const [mode, setMode] = useState<"choose" | "appointment">(editing ? "appointment" : "choose");
  const [title, setTitle] = useState(editing?.title ?? "");
  const [start, setStart] = useState(editing?.start ?? defaultStart ?? "09:00");
  const [minutes, setMinutes] = useState(editing ? editing.minutes : defaultStart ? 60 : 0);
  const [location, setLocation] = useState(editing?.location ?? "");
  // A length the list doesn't offer (an appointment made elsewhere) is offered as itself, so saving keeps it.
  const lengths = APPOINTMENT_LENGTHS.some((l) => l.minutes === minutes)
    ? APPOINTMENT_LENGTHS
    : [...APPOINTMENT_LENGTHS, { minutes, label: `${minutes} minutes` }].sort((a, b) => (a.minutes || 1e9) - (b.minutes || 1e9));
  const [sourceId, setSourceId] = useState(sources[0]?.id ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const add = async (e: React.FormEvent) => {
    e.preventDefault();
    const token = getAccessToken();
    if (!token || busy || !title.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const allDay = minutes === 0;
      const request = {
        segmentKey,
        title: title.trim(),
        startTime: allDay ? null : start,
        durationMinutes: allDay ? null : minutes,
        allDay,
        location: location.trim() || null,
        sourceId: !editing && sources.length > 1 ? sourceId : null,
      };
      const { itemKey } = editing
        ? await api.changeCalendarAppointment(token, instanceId, editing.itemKey, request)
        : await api.addCalendarAppointment(token, instanceId, request);
      onDone(itemKey);
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 404
          ? "Adding appointments isn't available on this dial just now."
          : err instanceof ApiError && err.message
            ? err.message
            : editing
              ? "The appointment couldn't be changed. Your calendar still has it as it was."
              : "The appointment couldn't be added. Nothing was put in your calendar.",
      );
      setBusy(false);
    }
  };

  return (
    <Modal title={editing ? "Change appointment" : `Add to ${dayLabel}`} onClose={() => onDone(null)} width={460}>
      {mode === "choose" ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <button type="button" className="kd-btn" onClick={() => setMode("appointment")} style={{ justifyContent: "flex-start", textAlign: "left" }}>
            <span style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <strong>Appointment in your calendar</strong>
              <span style={{ fontSize: 12.5, color: "var(--text-muted)" }}>
                Added to {sources.length === 1 ? sourceLabel(sources[0]) : "your calendar"}, and shown here like your other appointments.
              </span>
            </span>
          </button>
          <button type="button" className="kd-btn" onClick={onJustDial} style={{ justifyContent: "flex-start", textAlign: "left" }}>
            <span style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <strong>Just on this dial</strong>
              <span style={{ fontSize: 12.5, color: "var(--text-muted)" }}>A new {itemWord} here. Nothing goes in your calendar.</span>
            </span>
          </button>
        </div>
      ) : (
        <form onSubmit={(e) => void add(e)} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <label style={label}>
            What
            <input autoFocus value={title} maxLength={300} onChange={(e) => setTitle(e.target.value)} placeholder="Dentist" style={field} />
          </label>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <label style={{ ...label, flex: "1 1 120px" }}>
              How long
              <select value={minutes} onChange={(e) => setMinutes(Number(e.target.value))} style={field}>
                {lengths.map((l) => (
                  <option key={l.minutes} value={l.minutes}>{l.label}</option>
                ))}
              </select>
            </label>
            {minutes !== 0 && (
              <label style={{ ...label, flex: "1 1 110px" }}>
                Starts
                <input type="time" value={start} onChange={(e) => setStart(e.target.value)} required style={field} />
              </label>
            )}
          </div>
          <label style={label}>
            Where (optional)
            <input value={location} maxLength={300} onChange={(e) => setLocation(e.target.value)} style={field} />
          </label>
          {!editing && sources.length > 1 && (
            <label style={label}>
              Calendar
              <select value={sourceId} onChange={(e) => setSourceId(e.target.value)} style={field}>
                {sources.map((s) => (
                  <option key={s.id} value={s.id}>{sourceLabel(s)}</option>
                ))}
              </select>
            </label>
          )}
          {error && <div role="alert" style={{ fontSize: 13, color: "var(--danger, #b42318)" }}>{error}</div>}
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button type="button" className="kd-btn kd-btn-sm" onClick={() => (editing ? onDone(null) : setMode("choose"))} disabled={busy}>
              {editing ? "Cancel" : "Back"}
            </button>
            <button type="submit" className="kd-btn kd-btn-sm kd-btn-primary" disabled={busy || !title.trim()}>
              {busy ? (editing ? "Saving…" : "Adding…") : editing ? "Save" : "Add to calendar"}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}
